import React, { useEffect, useRef, useState } from "react";
import {
    Alert, Box, Breadcrumbs, Button, CircularProgress, Grid, Link, MenuItem,
    Paper, Snackbar, TextField, Typography, useMediaQuery, useTheme, Stack,
    InputAdornment, Divider, Card, CardContent, CssBaseline
} from "@mui/material";
import {
    NavigateNext, ArrowBack, Save, Clear, School, Event, AccessTime,
    Payments
} from '@mui/icons-material';
import { Form, Formik } from "formik";
import * as yup from "yup";
import Header from "../../component/header/Header";
import { useNavigate } from "react-router-dom";
import moment from "moment";

// Standardized Theme Colors
const colors = {
    pageBg: '#F8F9FA',
    sidebarBg: '#F1F2F6',
    accent: '#FFC107',
    accentDark: '#FFA000',
    tableHeader: '#FDF7E2',
    textMain: '#5D4037',
    white: '#FFFFFF',
    filterBg: "#FFF9E6"
};

const AddCourseScreen = () => {
    const navigation = useNavigate();
    const formikRef = useRef();

    // State Management
    const [classList, setClassList] = useState([]);
    const [tutorList, setTutorList] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [loadingSubmit, setLoadingSubmit] = useState(false);

    // Initial Data Loading
    useEffect(() => {
        getBatchList();
        getTutorList();
        localStorage.setItem("current_tab", "Class");
    }, []);

    // API: Fetch Staff/Tutors
    const getTutorList = async () => {
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/staff`);
            const resJson = await response.json();
            setTutorList(resJson.data || []);
        } catch (error) {
            console.log(error);
        }
    };

    // API: Fetch Batches
    const getBatchList = async () => {
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/batch`);
            const resJson = await response.json();
            setClassList(resJson.data || []);
        } catch (error) {
            console.log(error);
        }
    };

    // API: Create Course Submission
    const handleCreate = async (values, { resetForm }) => {
        setLoadingSubmit(true);
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/courses`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...values,
                    time: moment(values.time, "HH:mm").format("h:mm a"), // Format time for DB
                    status: "A", // Default Active
                })
            });
            const resJson = await response.json();

            if (resJson.status === 1) {
                setSnackbar({ open: true, message: "Class created successfully!", severity: "success" });
                resetForm();
                setTimeout(() => navigation("/Portal/Class"), 1500);
            } else {
                setSnackbar({ open: true, message: resJson.message, severity: "error" });
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Server connection failed", severity: "error" });
        } finally {
            setLoadingSubmit(false);
        }
    };

    // --- Validation Schema ---
    const validationSchema = yup.object({
        subject: yup.string().required("Subject is required"),
        class: yup.string().required("Batch is required"),
        group: yup.string().required("Group is required"),
        day: yup.string().required("Day is required"),
        time: yup.string().required("Time is required"),
        classRoom: yup.string().required("Classroom/Venue is required"),
        staffID: yup.string().required("Tutor assignment is required"),
        payment: yup.number().required("Payment is required").typeError("Payment must be a number"),
        description: yup.string().nullable(), // Optional
    });

    // Initial Form Values
    const initialValues = {
        courseName: "",
        day: "",
        time: "",
        classRoom: "",
        staffID: "",
        staffName: "",
        description: "",
        class: "",
        group: "",
        subject: "",
        payment: ""
    };

    // Full Lists from your original code
    const subjects = [
        "Bahasa Melayu", "English", "Matematik", "Sains", "Sejarah",
        "Akaun", "Perniagaan", "Ekonomi", "Add Math", "Biology",
        "Chemistry", "Physics", "Bahasa Arab", "Fardu Ain", "Mengaji",
        "Jawi", "Membaca", "Admin Checkin", "Admin Checkout"
    ];

    const groups = [
        "Kumpulan 1", "Kumpulan 2", "Kumpulan 3", "Kumpulan 4",
        "Kumpulan 5", "Kumpulan 6", "Kumpulan 7", "Kumpulan 8",
        "Kumpulan 9", "Kumpulan 10", "OTO"
    ];

    return (
        <Box sx={{ display: 'flex', bgcolor: colors.sidebarBg, minHeight: '100vh' }}>
            <CssBaseline />
            <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: "white", m: 2, borderRadius: 5 }}>

                {/* Breadcrumbs & Header */}
                <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
                    <Link underline="hover" color="inherit" onClick={() => navigation("/Portal")} sx={{ cursor: 'pointer' }}>Portal</Link>
                    <Link underline="hover" color="inherit" onClick={() => navigation("/Portal/Class")} sx={{ cursor: 'pointer' }}>Class</Link>
                    <Typography color="text.primary">Add New Class</Typography>
                </Breadcrumbs>

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Header title="Class Setup" subtitle="Create and configure new course sessions" />
                    <Button startIcon={<ArrowBack />} onClick={() => navigation(-1)} color="inherit">Back</Button>
                </Stack>

                {/* Form Logic */}
                <Formik
                    innerRef={formikRef}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleCreate}
                >
                    {({ values, errors, touched, handleChange, setFieldValue, handleBlur }) => {

                        // Auto-generate course name when subject, class, or group changes
                        const updateCourseName = (field, val) => {
                            const current = { ...values, [field]: val };
                            setFieldValue("courseName", `${current.subject} ${current.class} ${current.group}`.trim());
                        };

                        return (
                            <Form>
                                <Grid container spacing={3}>

                                    {/* --- SEGMENT 1: GENERAL INFORMATION (Full Width) --- */}
                                    <Grid item xs={12}>
                                        <Paper elevation={0} sx={{ p: 3, bgcolor: colors.filterBg, borderRadius: 3, border: '1px solid #F0E6CC' }}>
                                            <Stack direction="row" spacing={1} alignItems="center" mb={3}>
                                                <School sx={{ color: colors.textMain }} />
                                                <Typography variant="h6" fontWeight="bold">General Information</Typography>
                                            </Stack>
                                            <Grid container spacing={2}>

                                                {/* Subject Selection */}
                                                <Grid item xs={12} md={4}>
                                                    <Typography variant="body2" fontWeight="bold" mb={1}>Subject <span style={{ color: 'red' }}>*</span></Typography>
                                                    <TextField select fullWidth name="subject" value={values.subject} size="small"
                                                        onChange={(e) => { handleChange(e); updateCourseName("subject", e.target.value); }}
                                                        onBlur={handleBlur} error={touched.subject && !!errors.subject} helperText={touched.subject && errors.subject}
                                                        sx={{ bgcolor: 'white' }}
                                                    >
                                                        {subjects.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                                    </TextField>
                                                </Grid>

                                                {/* Batch Selection */}
                                                <Grid item xs={12} md={4}>
                                                    <Typography variant="body2" fontWeight="bold" mb={1}>Batch <span style={{ color: 'red' }}>*</span></Typography>
                                                    <TextField select fullWidth name="class" value={values.class} size="small"
                                                        onChange={(e) => { handleChange(e); updateCourseName("class", e.target.value); }}
                                                        onBlur={handleBlur} error={touched.class && !!errors.class} helperText={touched.class && errors.class}
                                                        sx={{ bgcolor: 'white' }}
                                                    >
                                                        {classList.filter(v => v.status === "A").map((v, i) => <MenuItem key={i} value={v.batchName}>{v.batchName}</MenuItem>)}
                                                    </TextField>
                                                </Grid>

                                                {/* Group Selection */}
                                                <Grid item xs={12} md={4}>
                                                    <Typography variant="body2" fontWeight="bold" mb={1}>Group <span style={{ color: 'red' }}>*</span></Typography>
                                                    <TextField select fullWidth name="group" value={values.group} size="small"
                                                        onChange={(e) => { handleChange(e); updateCourseName("group", e.target.value); }}
                                                        onBlur={handleBlur} error={touched.group && !!errors.group} helperText={touched.group && errors.group}
                                                        sx={{ bgcolor: 'white' }}
                                                    >
                                                        {groups.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                                                    </TextField>
                                                </Grid>

                                                {/* Auto-Generated Name Display */}
                                                <Grid item xs={12}>
                                                    <Typography variant="body2" fontWeight="bold" mb={1}>Full Class Name (Auto-generated)</Typography>
                                                    <TextField fullWidth name="courseName" value={values.courseName} disabled size="small" sx={{ bgcolor: '#f0f0f0' }} />
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    </Grid>

                                    {/* --- SEGMENT 2: SCHEDULE & VENUE (Left Column) --- */}
                                    <Grid item xs={12} md={6}>
                                        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #F0E6CC', height: '100%' }}>
                                            <Box sx={{ p: 2, bgcolor: colors.tableHeader, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Event fontSize="small" />
                                                <Typography fontWeight="bold">Schedule & Venue</Typography>
                                            </Box>
                                            <CardContent>
                                                <Stack spacing={2}>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="bold" mb={1}>Day <span style={{ color: 'red' }}>*</span></Typography>
                                                        <TextField select fullWidth name="day" value={values.day} onChange={handleChange} onBlur={handleBlur} error={touched.day && !!errors.day} helperText={touched.day && errors.day} size="small">
                                                            {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                                                        </TextField>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="bold" mb={1}>Start Time <span style={{ color: 'red' }}>*</span></Typography>
                                                        <TextField fullWidth name="time" type="time" value={values.time} onChange={handleChange} onBlur={handleBlur} error={touched.time && !!errors.time} helperText={touched.time && errors.time} size="small" InputLabelProps={{ shrink: true }} />
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="bold" mb={1}>Classroom / Room No <span style={{ color: 'red' }}>*</span></Typography>
                                                        <TextField fullWidth name="classRoom" value={values.classRoom} onChange={handleChange} onBlur={handleBlur} error={touched.classRoom && !!errors.classRoom} helperText={touched.classRoom && errors.classRoom} size="small" placeholder="e.g. Room 302" />
                                                    </Box>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    {/* --- SEGMENT 3: TUTOR & Paymenr (Right Column) --- */}
                                    <Grid item xs={12} md={6}>
                                        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #F0E6CC', height: '100%' }}>
                                            <Box sx={{ p: 2, bgcolor: colors.tableHeader, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Payments fontSize="small" />
                                                <Typography fontWeight="bold">Tutor & Payment</Typography>
                                            </Box>
                                            <CardContent>
                                                <Stack spacing={2}>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="bold" mb={1}>Assign Tutor <span style={{ color: 'red' }}>*</span></Typography>
                                                        <TextField select fullWidth name="staffID" value={values.staffID} size="small"
                                                            onChange={(e) => {
                                                                handleChange(e);
                                                                setFieldValue("staffName", tutorList.find(t => t.staffId === e.target.value)?.fullname || "");
                                                            }}
                                                            onBlur={handleBlur} error={touched.staffID && !!errors.staffID} helperText={touched.staffID && errors.staffID}
                                                        >
                                                            {tutorList.filter(v => v.role === "Tutor").map((v, i) => <MenuItem key={i} value={v.staffId}>{v.fullname}</MenuItem>)}
                                                        </TextField>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="bold" mb={1}>Payment per class <span style={{ color: 'red' }}>*</span></Typography>
                                                        <TextField fullWidth name="payment" type="number" value={values.payment} onChange={handleChange} onBlur={handleBlur} error={touched.payment && !!errors.payment} helperText={touched.payment && errors.payment} size="small"
                                                            InputProps={{ startAdornment: <InputAdornment position="start">RM</InputAdornment> }}
                                                        />
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="bold" mb={1}>Internal Notes (Optional)</Typography>
                                                        <TextField fullWidth name="description" value={values.description} onChange={handleChange} size="small" placeholder="Notes for admin..." />
                                                    </Box>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    {/* --- ACTION BUTTONS --- */}
                                    <Grid item xs={12}>
                                        <Divider sx={{ my: 2 }} />
                                        <Stack direction="row" spacing={2} justifyContent="flex-end">
                                            <Button variant="outlined" onClick={() => navigation(-1)} startIcon={<Clear />} sx={{ borderRadius: 2 }}>Discard</Button>
                                            <Button
                                                type="submit" variant="contained" disabled={loadingSubmit}
                                                startIcon={loadingSubmit ? <CircularProgress size={20} color="inherit" /> : <Save />}
                                                sx={{ bgcolor: colors.accent, color: 'black', px: 6, borderRadius: 2, '&:hover': { bgcolor: colors.accentDark } }}
                                            >
                                                {loadingSubmit ? "Saving..." : "Create Class"}
                                            </Button>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Form>
                        );
                    }}
                </Formik>

                {/* Feedback Snackbar */}
                <Snackbar
                    open={snackbar.open} autoHideDuration={4000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>{snackbar.message}</Alert>
                </Snackbar>
            </Box>
        </Box>
    );
};

export default AddCourseScreen;